import type { IdentityTranslate } from '../i18n';
import { Button, MoneyInput, Select, TextInput, Checkbox } from '../../../shell/kit';
import type { FieldCatalog, SegmentField } from '../api';

export type Node = GroupNode | RuleNode;

export interface GroupNode {
  kind: 'group';
  op: 'and' | 'or';
  not: boolean;
  nodes: Node[];
}

export type RuleNode =
  | { kind: 'rfm'; metric: string; windowDays: number | null; op: string; value: number }
  | { kind: 'num'; field: string; op: string; value: number }
  | { kind: 'enum'; field: string; op: string; values: string[] }
  | { kind: 'bool'; field: string; value: boolean }
  | { kind: 'text'; field: string; op: string; value: string };

const RFM_METRICS = [
  'recency_days',
  'visit_count',
  'paid_order_count',
  'monetary_minor',
  'avg_check_minor',
] as const;

const NUM_FIELDS = ['loyalty_balance_minor', 'lifetime_days', 'churn_risk_pct'] as const;

const ENUM_FIELDS = [
  'customer_type',
  'customer_source',
  'customer_status',
  'loyalty_tier_code',
  'vehicle_size_class',
  'vehicle_make',
  'visited_site_id',
] as const;

const BOOL_FIELDS = ['phone_verified', 'has_email', 'has_vehicle', 'has_visits'] as const;

const COMPARE_OPS = ['eq', 'ne', 'lt', 'lte', 'gt', 'gte'] as const;

interface FieldChoice {
  readonly id: string;
  readonly group: 'rfm' | 'num' | 'enum' | 'bool' | 'text';
}

export const FIELD_CHOICES: readonly FieldChoice[] = [
  ...RFM_METRICS.map((id) => ({ id, group: 'rfm' as const })),
  ...NUM_FIELDS.map((id) => ({ id, group: 'num' as const })),
  ...ENUM_FIELDS.map((id) => ({ id, group: 'enum' as const })),
  ...BOOL_FIELDS.map((id) => ({ id, group: 'bool' as const })),
  { id: 'display_name', group: 'text' as const },
];

export function emptyRule(): RuleNode {
  return { kind: 'rfm', metric: 'recency_days', windowDays: null, op: 'lte', value: 30 };
}

export function emptyGroup(): GroupNode {
  return { kind: 'group', op: 'and', not: false, nodes: [emptyRule()] };
}

function ruleForField(choice: FieldChoice): RuleNode {
  if (choice.group === 'rfm') {
    return { kind: 'rfm', metric: choice.id, windowDays: null, op: 'gte', value: 1 };
  }
  if (choice.group === 'num') return { kind: 'num', field: choice.id, op: 'gte', value: 0 };
  if (choice.group === 'enum') return { kind: 'enum', field: choice.id, op: 'in', values: [] };
  if (choice.group === 'bool') return { kind: 'bool', field: choice.id, value: true };
  return { kind: 'text', field: choice.id, op: 'contains', value: '' };
}

function fieldIdOf(node: RuleNode): string {
  return node.kind === 'rfm' ? node.metric : node.field;
}

/**
 * Денежные поля конструктора хранятся в тиынах, как и всё в базе, но вводить
 * их маркетологу нужно в тенге: «сумма покупок ≥ 50000» читается как пятьдесят
 * тысяч тенге, а означало пятьсот. Ошибка в сто раз в условии сегмента — это
 * рассылка не тем людям.
 */
const MONEY_FIELDS = new Set(['monetary_minor', 'avg_check_minor', 'loyalty_balance_minor']);

function RuleRow({
  node,
  t,
  catalog,
  locale,
  onChange,
  onRemove,
}: {
  node: RuleNode;
  t: IdentityTranslate;
  catalog: FieldCatalog | null;
  locale: 'ru' | 'kk';
  onChange: (next: RuleNode) => void;
  onRemove: () => void;
}) {
  const currentField = fieldIdOf(node);
  const spec: SegmentField | undefined = catalog?.fields.find((f) => f.id === currentField);
  const options = spec?.options ?? [];
  const label = (o: { titleRu: string; titleKk: string }): string =>
    locale === 'kk' ? o.titleKk : o.titleRu;

  return (
    <div className="flex flex-col gap-1 rounded border border-line bg-sunken p-2">
      <div className="flex flex-wrap items-end gap-2">
      <label className="flex flex-col gap-1 text-xs">
        <span className="text-ink-muted">{t('identity.segments.field')}</span>
        <Select
          value={currentField}
          onChange={(e) => {
            const choice = FIELD_CHOICES.find((f) => f.id === e.target.value);
            if (choice) onChange(ruleForField(choice));
          }}
        >
          {/*
            Поля сгруппированы по смыслу: плоский список из семнадцати строк
            заставлял читать всё подряд, чтобы найти «сумму покупок».
          */}
          {(catalog?.groups ?? []).map((group) => (
            <optgroup key={group.id} label={label(group)}>
              {FIELD_CHOICES.filter(
                (f) => catalog?.fields.find((x) => x.id === f.id)?.group === group.id,
              ).map((f) => (
                <option key={f.id} value={f.id}>
                  {t(`identity.field.${f.id}` as never)}
                </option>
              ))}
            </optgroup>
          ))}
          {catalog === null
            ? FIELD_CHOICES.map((f) => (
                <option key={f.id} value={f.id}>
                  {t(`identity.field.${f.id}` as never)}
                </option>
              ))
            : null}
        </Select>
      </label>

      {node.kind === 'rfm' && node.metric !== 'recency_days' ? (
        <label className="flex flex-col gap-1 text-xs">
          <span className="text-ink-muted">{t('identity.segments.window')}</span>
          <TextInput
            type="number"
            className="w-24"
            value={node.windowDays ?? ''}
            placeholder="∞"
            onChange={(e) =>
              onChange({
                ...node,
                windowDays: e.target.value === '' ? null : Number(e.target.value),
              })
            }
          />
        </label>
      ) : null}

      {node.kind === 'bool' ? (
        <label className="flex flex-col gap-1 text-xs">
          <span className="text-ink-muted">{t('identity.segments.value')}</span>
          <Select
            value={node.value ? 'yes' : 'no'}
            onChange={(e) => onChange({ ...node, value: e.target.value === 'yes' })}
          >
            <option value="yes">{t('identity.op.yes')}</option>
            <option value="no">{t('identity.op.no')}</option>
          </Select>
        </label>
      ) : (
        <label className="flex flex-col gap-1 text-xs">
          <span className="text-ink-muted">{t('identity.segments.operator')}</span>
          <Select
            value={node.op}
            onChange={(e) => onChange({ ...node, op: e.target.value } as RuleNode)}
          >
            {(node.kind === 'enum'
              ? (['in', 'not_in'] as const)
              : node.kind === 'text'
                ? (['eq', 'contains', 'starts_with'] as const)
                : COMPARE_OPS
            ).map((op) => (
              <option key={op} value={op}>
                {t(`identity.op.${op}` as never)}
              </option>
            ))}
          </Select>
        </label>
      )}

      {node.kind === 'enum' ? (
        <div className="flex flex-1 flex-col gap-1 text-xs">
          <span className="text-ink-muted">{t('identity.segments.value')}</span>
          {options.length > 0 ? (
            /*
              Значения выбираются, а не набираются. Раньше здесь была строка
              «a, b, c», в которую надо было вписать `individual` или ULID
              объекта: ошибка давала пустой сегмент без единого слова о том,
              почему он пуст.
            */
            <div className="flex flex-wrap gap-2">
              {options.map((option) => {
                const on = node.values.includes(option.value);
                return (
                  <label
                    key={option.value}
                    className={`flex cursor-pointer items-center gap-1 rounded-control border px-2 py-1 ${
                      on ? 'border-brand-300 bg-brand-50' : 'border-line'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={on}
                      onChange={() =>
                        onChange({
                          ...node,
                          values: on
                            ? node.values.filter((v) => v !== option.value)
                            : [...node.values, option.value],
                        })
                      }
                    />
                    {label(option)}
                  </label>
                );
              })}
            </div>
          ) : (
            <TextInput
              value={node.values.join(', ')}
              onChange={(e) =>
                onChange({
                  ...node,
                  values: e.target.value
                    .split(',')
                    .map((v) => v.trim())
                    .filter((v) => v !== ''),
                })
              }
            />
          )}
        </div>
      ) : null}

      {node.kind === 'text' ? (
        <label className="flex flex-1 flex-col gap-1 text-xs">
          <span className="text-ink-muted">{t('identity.segments.value')}</span>
          <TextInput
            value={node.value}
            onChange={(e) => onChange({ ...node, value: e.target.value })}
          />
        </label>
      ) : null}

      {node.kind === 'rfm' || node.kind === 'num' ? (
        <label className="flex flex-col gap-1 text-xs">
          <span className="text-ink-muted">{t('identity.segments.value')}</span>
          {MONEY_FIELDS.has(currentField) ? (
            <MoneyInput
              className="w-32"
              valueMinor={node.value}
              onChangeMinor={(minor) => onChange({ ...node, value: minor ?? 0 })}
            />
          ) : (
            <TextInput
              type="number"
              className="w-32"
              value={node.value}
              onChange={(e) => onChange({ ...node, value: Number(e.target.value) })}
            />
          )}
        </label>
      ) : null}

      <Button onClick={onRemove}>{t('identity.segments.remove')}</Button>
      </div>
      {spec ? (
        <p className="text-ink-faint text-xs">{locale === 'kk' ? spec.hintKk : spec.hintRu}</p>
      ) : null}
    </div>
  );
}

export function GroupEditor({
  node,
  t,
  catalog,
  locale,
  depth = 0,
  onChange,
  onRemove,
}: {
  node: GroupNode;
  t: IdentityTranslate;
  catalog: FieldCatalog | null;
  locale: 'ru' | 'kk';
  depth?: number;
  onChange: (next: GroupNode) => void;
  onRemove?: () => void;
}) {
  const update = (index: number, next: Node | null): void => {
    const nodes = [...node.nodes];
    if (next === null) nodes.splice(index, 1);
    else nodes[index] = next;
    onChange({ ...node, nodes });
  };

  return (
    <div
      className={`space-y-2 rounded border p-3 ${depth === 0 ? 'border-line-strong' : 'border-line bg-surface'}`}
    >
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs text-ink-muted">{t('identity.segments.match')}</span>
        <Select
          value={node.op}
          onChange={(e) => onChange({ ...node, op: e.target.value as 'and' | 'or' })}
        >
          <option value="and">{t('identity.segments.all')}</option>
          <option value="or">{t('identity.segments.any')}</option>
        </Select>
        <label className="flex items-center gap-1 text-xs text-ink-muted">
          <Checkbox
            checked={node.not}
            onChange={(e) => onChange({ ...node, not: e.target.checked })}
          />
          NOT
        </label>
        <div className="ml-auto flex gap-2">
          <Button onClick={() => onChange({ ...node, nodes: [...node.nodes, emptyRule()] })}>
            {t('identity.segments.addRule')}
          </Button>
          {depth < 3 ? (
            <Button onClick={() => onChange({ ...node, nodes: [...node.nodes, emptyGroup()] })}>
              {t('identity.segments.addGroup')}
            </Button>
          ) : null}
          {onRemove ? <Button onClick={onRemove}>{t('identity.segments.remove')}</Button> : null}
        </div>
      </div>

      {node.nodes.map((child, index) =>
        child.kind === 'group' ? (
          <GroupEditor
            catalog={catalog}
            locale={locale}
            key={index}
            node={child}
            t={t}
            depth={depth + 1}
            onChange={(next) => update(index, next)}
            onRemove={() => update(index, null)}
          />
        ) : (
          <RuleRow
            catalog={catalog}
            locale={locale}
            key={index}
            node={child}
            t={t}
            onChange={(next) => update(index, next)}
            onRemove={() => update(index, null)}
          />
        ),
      )}
    </div>
  );
}

export function toDefinition(root: GroupNode): unknown {
  return { version: 1, root };
}
